import { useContext, useMemo } from 'react';
import { COOKIES } from '~/constants/srp';
import SearchSource from '~/constants/srp/SearchSource';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import { useFinanceInfo } from '~/context/srp/FinanceContext';
import { usePagination, useRequestIds } from '~/context/srp/InventoryContext';
import { useTradeInfo } from '~/context/srp/SearchContext';
import {
  useRefSource,
  useSearchSource
} from '~/context/srp/SearchEventsDataContext';
import useGetSortByChanged from '~/hooks/srp/useGetSortByChanged';
import usePriceDrop from '~/hooks/srp/usePriceDrop';
import BrowserCookies from '~/utilities/BrowserCookies';
import replaceUndefinedWithNull from '~/utilities/srp/replaceUndefinedWithNull';
import { MegaUserInfo } from '../../types';

export default function useGetUserTileInfo() {
  const { appliedFilters, sortBy } = useContext(AppliedFiltersContext);
  const refSource = useRefSource();
  const searchSource = useSearchSource();
  const financeInfo = useFinanceInfo();
  const { searchRequestId, previousSearchRequestId } = useRequestIds();
  const { currentPage, totalMatchedInventory = 0 } = usePagination();
  const { restrictedState: isPriceDropRestrictedState } = usePriceDrop();
  const tradeInfo = useTradeInfo();
  const isDefaultFilters = Object.keys(appliedFilters).length === 0;
  const isInitialSortBy = useGetSortByChanged();
  const isOpenSearch = isDefaultFilters && isInitialSortBy;
  const { platform, isNative } = useNativeDeviceType();
  const { zip } = useUserLocation();

  return useMemo<MegaUserInfo>(
    () =>
      replaceUndefinedWithNull({
        appliedFilters,
        currentPage,
        financeInfo,
        isOpenSearch,
        isPriceDropRestrictedState,
        isWebview: isNative,
        webview: isNative
          ? { platform, version: BrowserCookies.get(COOKIES.nativeAppVersion) }
          : null,
        previousSearchRequestId,
        refSource:
          searchSource === SearchSource.InitialPageLoad ? refSource : 'srp',
        searchRequestId,
        searchSource,
        sortBy,
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          status:
            tradeInfo?.status && tradeInfo?.totalTradeInValue === 0
              ? 'offer pending'
              : tradeInfo?.status,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue
        },
        zip: zip
      }),
    [
      appliedFilters,
      currentPage,
      financeInfo,
      isNative,
      isOpenSearch,
      isPriceDropRestrictedState,
      platform,
      previousSearchRequestId,
      refSource,
      searchRequestId,
      searchSource,
      sortBy,
      totalMatchedInventory,
      tradeInfo,
      zip
    ]
  );
}
